import React, { useState, useEffect, useRef } from 'react';
import {
  Activity,
  Server,
  Database,
  AlertTriangle,
  CheckCircle,
  Clock,
  Cpu,
  HardDrive,
  Network,
  Zap,
  Settings,
  Play,
  RefreshCw,
  ChevronRight,
  Phone,
  PhoneCall,
  Plus
} from 'lucide-react';
import api from '../services/api';

const MonitoringPage = () => {
  const [systemStatus, setSystemStatus] = useState({
    overall: 'healthy',
    services: [],
    metrics: {},
    alerts: []
  });

  const [realTimeData, setRealTimeData] = useState({
    activeCalls: 0,
    cpuUsage: 0,
    memoryUsage: 0,
    diskUsage: 0,
    networkIn: 0,
    networkOut: 0
  });

  const [loading, setLoading] = useState(true);
  const [pipelineConfig, setPipelineConfig] = useState(null);
  const [pipelineHealth, setPipelineHealth] = useState(null);
  const [testingPipeline, setTestingPipeline] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testNumbers, setTestNumbers] = useState([]);
  const [vonageNumbers, setVonageNumbers] = useState([]);
  const [fromNumber, setFromNumber] = useState('');
  const [toNumber, setToNumber] = useState('');
  const [triggeringCall, setTriggeringCall] = useState(false);
  const [callResult, setCallResult] = useState(null);
  const [voiceConfig, setVoiceConfig] = useState({
    voiceModel: 'alloy',
    systemPrompt: ''
  });
  const [scriptConfig, setScriptConfig] = useState({
    introMessage: '',
    questions: ['', ''],
    closingMessage: ''
  });
  const scriptDefaultsApplied = useRef(false);

  // Call Queue States
  const [activeQueueTab, setActiveQueueTab] = useState('queue'); // queue, manage, results
  const [queuedCalls, setQueuedCalls] = useState([]);
  const [queueResults, setQueueResults] = useState([]);
  const [loadingQueue, setLoadingQueue] = useState(false);
  const [newQueueCall, setNewQueueCall] = useState({
    name: '',
    description: '',
    message: '',
    scheduledFor: ''
  });

  useEffect(() => {
    fetchSystemStatus();
    fetchPipelineConfig();
    fetchPipelineHealth();
    fetchTestNumbers();
    fetchQueuedCalls();
    fetchQueueResults();
    const interval = setInterval(() => {
      fetchRealTimeMetrics();
      fetchPipelineHealth();
      if (activeQueueTab === 'manage') {
        fetchQueuedCalls();
      } else if (activeQueueTab === 'results') {
        fetchQueueResults();
      }
    }, 5000);
    return () => clearInterval(interval);
  }, [activeQueueTab]);

  const fetchSystemStatus = async () => {
    try {
      const response = await api.get('/health/status');
      setSystemStatus(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Failed to fetch system status:', error);
      // Set minimal status on error
      setSystemStatus({
        overall: 'unknown',
        services: [],
        metrics: {},
        alerts: []
      });
      setLoading(false);
    }
  };

  const fetchRealTimeMetrics = async () => {
    try {
      const response = await api.get('/health/metrics');
      setRealTimeData(response.data);
    } catch (error) {
      console.error('Failed to fetch real-time metrics:', error);
      // Keep previous data on error
    }
  };

  const fetchPipelineConfig = async () => {
    try {
      const response = await api.get('/pipeline/config');
      setPipelineConfig(response.data.pipeline);
    } catch (error) {
      console.error('Failed to fetch pipeline config:', error);
      // Set error state so UI can show error message
      setPipelineConfig({ error: error.response?.data?.message || error.message || 'Failed to load pipeline config' });
    }
  };

  // Pipeline layer switching is managed via Admin Dashboard provider overrides
  // Layers: 1 (OpenAI Realtime), 2 (Claude + AssemblyAI), 3 (Claude + ElevenLabs), Emergency (Vonage IVR)

  const fetchPipelineHealth = async () => {
    try {
      const response = await api.get('/pipeline/health');
      setPipelineHealth(response.data.health);
    } catch (error) {
      console.error('Failed to fetch pipeline health:', error);
    }
  };

  const fetchTestNumbers = async () => {
    try {
      const response = await api.get('/monitoring/test-numbers');
      const allNumbers = response.data.data.numbers || [];
      const mainNumber = response.data.data.fromNumber || '';

      // Separate Vonage numbers (including main and Aaron)
      const vonageNums = [
        { id: 'main', label: 'Main Number', number: mainNumber, description: 'Primary business line' },
        ...allNumbers.filter(n => n.id === 'aaron' || n.id.startsWith('vonage_'))
      ];

      setVonageNumbers(vonageNums);
      setTestNumbers(allNumbers);

      // Set defaults: Main calls Aaron (outgoing call)
      if (vonageNums.length >= 2) {
        setFromNumber(mainNumber);  // Main number as caller
        const aaronNum = allNumbers.find(n => n.id === 'aaron');
        if (aaronNum) {
          setToNumber(aaronNum.number);  // Aaron as receiver
        }
      }
    } catch (error) {
      console.error('Failed to fetch test numbers:', error);
    }
  };

  // Call Queue Management Functions
  const fetchQueuedCalls = async () => {
    try {
      setLoadingQueue(true);
      const response = await api.get('/test-calls/queue?status=pending&limit=50');
      setQueuedCalls(response.data.data.queuedCalls || []);
    } catch (error) {
      console.error('Failed to fetch queued calls:', error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const fetchQueueResults = async () => {
    try {
      setLoadingQueue(true);
      const response = await api.get('/test-calls/results?limit=50');
      setQueueResults(response.data.data.results || []);
    } catch (error) {
      console.error('Failed to fetch queue results:', error);
    } finally {
      setLoadingQueue(false);
    }
  };

  const addToQueue = async () => {
    if (!newQueueCall.name || !newQueueCall.message) {
      setCallResult({
        success: false,
        error: 'Name and message are required'
      });
      return;
    }

    try {
      const aaronNumber = testNumbers.find(n => n.id === 'aaron')?.number;
      const mainNumber = vonageNumbers.find(n => n.id === 'main')?.number;

      if (!aaronNumber || !mainNumber) {
        setCallResult({
          success: false,
          error: 'Phone numbers not configured'
        });
        return;
      }

      const payload = {
        name: newQueueCall.name,
        description: newQueueCall.description,
        fromNumber: aaronNumber, // Aaron calls the system
        toNumber: mainNumber, // System receives the call
        priority: 1,
        scheduledFor: newQueueCall.scheduledFor || null,
        flows: [
          {
            stepNumber: 1,
            stepType: 'initial',
            questionText: newQueueCall.message,
            expectedKeywords: [],
            conditionLogic: null,
            timeout: 60
          }
        ]
      };

      const response = await api.post('/test-calls/queue', payload);

      setCallResult({
        success: true,
        data: {
          message: 'Call added to queue successfully',
          queueId: response.data.data.id
        }
      });

      // Reset form
      setNewQueueCall({
        name: '',
        description: '',
        message: '',
        scheduledFor: ''
      });

      // Refresh queue
      fetchQueuedCalls();

    } catch (error) {
      setCallResult({
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to queue call'
      });
    }
  };

  const executeQueuedCall = async (queueId) => {
    try {
      await api.post(`/test-calls/queue/${queueId}/execute`);
      setCallResult({
        success: true,
        data: {
          message: 'Call execution started'
        }
      });
      // Refresh queue after a delay
      setTimeout(fetchQueuedCalls, 2000);
    } catch (error) {
      setCallResult({
        success: false,
        error: error.response?.data?.message || 'Failed to execute call'
      });
    }
  };

  const deleteQueuedCall = async (queueId) => {
    try {
      await api.delete(`/test-calls/queue/${queueId}`);
      setCallResult({
        success: true,
        data: {
          message: 'Call removed from queue'
        }
      });
      fetchQueuedCalls();
    } catch (error) {
      setCallResult({
        success: false,
        error: error.response?.data?.message || 'Failed to delete call'
      });
    }
  };

  const triggerTestCall = async () => {
    if (!fromNumber || !toNumber) {
      setCallResult({
        success: false,
        error: 'Please select both caller and receiver numbers'
      });
      return;
    }

    if (fromNumber === toNumber) {
      setCallResult({
        success: false,
        error: 'Caller and receiver cannot be the same number'
      });
      return;
    }

    setTriggeringCall(true);
    setCallResult(null);

    try {
      const preparedScript = {
        introMessage: scriptConfig.introMessage?.trim() || null,
        questions: scriptConfig.questions
          .map(question => question.trim())
          .filter(question => question.length > 0),
        closingMessage: scriptConfig.closingMessage?.trim() || null
      };

      let payloadVoiceConfig = fromNumber.includes('2096800721')
        ? {
          voiceModel: voiceConfig.voiceModel,
          systemPrompt: voiceConfig.systemPrompt?.trim() || ''
        }
        : null;

      const autoPrompt = buildSystemPromptFromScript(preparedScript, payloadVoiceConfig?.voiceModel);

      if (payloadVoiceConfig && !payloadVoiceConfig.systemPrompt && autoPrompt) {
        payloadVoiceConfig = {
          ...payloadVoiceConfig,
          systemPrompt: autoPrompt
        };
      }

      const payload = {
        fromNumber,
        toNumber,
        callType: fromNumber.includes('2096800721') ? 'incoming_test' : 'outgoing_test',
        script: preparedScript,
        customGreeting: preparedScript.introMessage || null
      };

      if (payloadVoiceConfig) {
        payload.voiceConfig = payloadVoiceConfig;
      }

      const response = await api.post('/monitoring/trigger-call', payload);

      setCallResult({
        success: true,
        data: response.data.data
      });
    } catch (error) {
      setCallResult({
        success: false,
        error: error.response?.data?.message || error.message || 'Failed to trigger call'
      });
    } finally {
      setTriggeringCall(false);
    }
  };

  const buildSystemPromptFromScript = (script, voiceModel) => {
    if (!script.introMessage && (!script.questions || script.questions.length === 0) && !script.closingMessage) {
      return null;
    }

    const lines = ['You are running a scripted monitoring call to validate the phone system.'];

    if (voiceModel) {
      lines.push(`Maintain the speaking characteristics of the ${voiceModel} voice.`);
    }

    if (script.introMessage) {
      lines.push(`Start with this greeting (or a natural variation): "${script.introMessage}"`);
    }

    if (script.questions && script.questions.length > 0) {
      lines.push('Ask the following questions one at a time. After each question, pause for the answer, acknowledge what you heard, and note any issues:');
      script.questions.forEach((question, index) => {
        lines.push(`${index + 1}. ${question}`);
      });
    }

    if (script.closingMessage) {
      lines.push(`Close with: "${script.closingMessage}"`);
    } else {
      lines.push('When the questions are complete, thank the callee for assisting with the test and let them know the call is ending.');
    }

    lines.push('Confirm that the callee can hear each part of any two-part question, keep the conversation natural, and do not repeat the opening greeting after the start of the call.');

    return lines.join('\n');
  };

  const updateScriptQuestion = (index, value) => {
    setScriptConfig(prev => {
      const updated = [...prev.questions];
      updated[index] = value;
      return { ...prev, questions: updated };
    });
  };

  useEffect(() => {
    if (fromNumber && fromNumber.includes('2096800721')) {
      if (!scriptDefaultsApplied.current) {
        setScriptConfig({
          introMessage: 'Hi, this is the Alfred Voice QA assistant running a quick system check.',
          questions: [
            'Can you confirm you heard the greeting and understand this first question?',
            'Do you hear this second part of the question clearly as well?'
          ],
          closingMessage: 'Thanks for confirming. This completes the monitoring test call.'
        });
        scriptDefaultsApplied.current = true;
      }
    } else {
      scriptDefaultsApplied.current = false;
    }
  }, [fromNumber]);

  const testPipeline = async (type = 'full') => {
    setTestingPipeline(true);
    setTestResult(null);
    try {
      const response = await api.post(`/pipeline/test/${type}`, {
        message: 'I would like to leave a message for Brian'
      });
      setTestResult({
        success: true,
        data: response.data
      });
    } catch (error) {
      setTestResult({
        success: false,
        error: error.response?.data?.error || error.message
      });
    } finally {
      setTestingPipeline(false);
    }
  };


  const getStatusColor = (status) => {
    switch (status) {
      case 'healthy': return 'text-green-600 bg-green-100';
      case 'warning': return 'text-yellow-600 bg-yellow-100';
      case 'error': return 'text-red-600 bg-red-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'healthy': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'error': return <AlertTriangle className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getAlertColor = (level) => {
    switch (level) {
      case 'error': return 'border-red-200 bg-red-50';
      case 'warning': return 'border-yellow-200 bg-yellow-50';
      case 'info': return 'border-blue-200 bg-blue-50';
      default: return 'border-gray-200 bg-gray-50';
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="bg-gray-200 h-32 rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
        <div className={`flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium ${getStatusColor(systemStatus.overall)}`}>
          {getStatusIcon(systemStatus.overall)}
          <span className="capitalize">{systemStatus.overall}</span>
        </div>
      </div>

      {/* Pipeline Configuration & Testing */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Settings className="w-5 h-5 text-gray-600" />
            <h2 className="text-lg font-semibold text-gray-900">AI Pipeline Configuration</h2>
          </div>
          <button
            onClick={() => {
              fetchPipelineConfig();
              fetchPipelineHealth();
            }}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            title="Refresh pipeline status"
          >
            <RefreshCw className="w-4 h-4 text-gray-600" />
          </button>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Current Configuration */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Current Pipeline</h3>
              {pipelineConfig?.error ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-800 mb-2">Failed to load pipeline configuration</p>
                  <p className="text-xs text-red-600">{pipelineConfig.error}</p>
                  <button
                    onClick={() => fetchPipelineConfig()}
                    className="mt-3 text-xs text-red-700 underline hover:text-red-900"
                  >
                    Retry
                  </button>
                </div>
              ) : pipelineConfig ? (
                <div className="space-y-3">
                  {/* Check if hybrid mode (Realtime API) */}
                  {pipelineConfig.mode === 'hybrid' ? (
                    <>
                      {/* Primary: Realtime API for English */}
                      <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border-2 border-green-300 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center space-x-2">
                            <Zap className="w-5 h-5 text-green-600" />
                            <p className="text-xs font-bold text-green-900 uppercase">Primary Mode (English)</p>
                          </div>
                          <div className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-700">
                            Active
                          </div>
                        </div>
                        <p className="text-sm font-bold text-gray-900 mb-1">{pipelineConfig.primary.name}</p>
                        <p className="text-xs text-gray-600 mb-2">{pipelineConfig.primary.description}</p>
                        <div className="flex flex-wrap gap-1 mb-2">
                          {pipelineConfig.primary.features.map((feature, idx) => (
                            <span key={idx} className="px-2 py-0.5 bg-green-100 text-green-700 text-xs rounded">
                              {feature}
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-gray-600">
                          <strong>Model:</strong> {pipelineConfig.primary.model} | <strong>Latency:</strong> {pipelineConfig.primary.latency}
                        </div>
                      </div>

                      {/* Fallback: Traditional Pipeline for Spanish */}
                      <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-semibold text-gray-700">Fallback Mode (Spanish)</p>
                          <div className="px-2 py-0.5 rounded-full text-xs font-medium bg-gray-200 text-gray-700">
                            Standby
                          </div>
                        </div>
                        <div className="space-y-1 text-xs">
                          <p><strong>AI:</strong> {pipelineConfig.fallback.ai?.provider?.toUpperCase() || 'N/A'}</p>
                          <p><strong>STT:</strong> {pipelineConfig.fallback.stt?.provider?.toUpperCase() || 'N/A'}</p>
                          <p><strong>TTS:</strong> {pipelineConfig.fallback.tts?.provider?.toUpperCase() || 'N/A'}</p>
                        </div>
                      </div>

                      {/* Routing Info */}
                      <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-xs font-semibold text-blue-900 mb-2">Language-Based Routing</p>
                        <div className="space-y-1 text-xs text-blue-800">
                          {Object.entries(pipelineConfig.routing).map(([lang, route]) => (
                            <p key={lang}>
                              <strong className="capitalize">{lang}:</strong> {route}
                            </p>
                          ))}
                        </div>
                      </div>
                    </>
                  ) : (
                    /* Legacy format - traditional pipeline only */
                    <>
                      <div className="flex items-center justify-between p-3 bg-blue-50 border border-blue-200 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-600">AI Provider</p>
                          <p className="text-sm font-semibold text-gray-900 uppercase">{pipelineConfig.ai?.provider}</p>
                        </div>
                        {pipelineHealth?.services?.ai && (
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pipelineHealth.services.ai.status === 'available'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {pipelineHealth.services.ai.status}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-600">STT Provider</p>
                          <p className="text-sm font-semibold text-gray-900 uppercase">{pipelineConfig.stt?.provider}</p>
                        </div>
                        {pipelineHealth?.services?.stt && (
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pipelineHealth.services.stt.status === 'available'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {pipelineHealth.services.stt.status}
                          </div>
                        )}
                      </div>
                      <div className="flex items-center justify-between p-3 bg-orange-50 border border-orange-200 rounded-lg">
                        <div>
                          <p className="text-xs text-gray-600">TTS Provider</p>
                          <p className="text-sm font-semibold text-gray-900 uppercase">{pipelineConfig.tts?.provider}</p>
                        </div>
                        {pipelineHealth?.services?.tts && (
                          <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                            pipelineHealth.services.tts.status === 'available'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {pipelineHealth.services.tts.status}
                          </div>
                        )}
                      </div>
                      <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-600 mb-1">Pipeline Description</p>
                        <p className="text-sm font-medium text-gray-900">{pipelineConfig.description}</p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading pipeline configuration...</p>
                </div>
              )}
            </div>

            {/* Pipeline Testing */}
            <div>
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Test Pipeline</h3>
              <div className="space-y-3">
                <button
                  onClick={() => testPipeline('ai')}
                  disabled={testingPipeline}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Zap className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">Test AI Provider</p>
                      <p className="text-xs text-gray-500">Generate AI response</p>
                    </div>
                  </div>
                  <Play className="w-4 h-4 text-gray-400" />
                </button>

                <button
                  onClick={() => testPipeline('tts')}
                  disabled={testingPipeline}
                  className="w-full flex items-center justify-between p-3 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Activity className="w-5 h-5 text-orange-600" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium text-gray-900">Test TTS Provider</p>
                      <p className="text-xs text-gray-500">Generate speech audio</p>
                    </div>
                  </div>
                  <Play className="w-4 h-4 text-gray-400" />
                </button>

                <button
                  onClick={() => testPipeline('full')}
                  disabled={testingPipeline}
                  className="w-full flex items-center justify-between p-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                      <ChevronRight className="w-5 h-5 text-white" />
                    </div>
                    <div className="text-left">
                      <p className="text-sm font-medium">Test Full Pipeline</p>
                      <p className="text-xs opacity-90">AI → TTS (End-to-end)</p>
                    </div>
                  </div>
                  {testingPipeline ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  ) : (
                    <Play className="w-4 h-4" />
                  )}
                </button>

                {/* Test Results */}
                {testResult && (
                  <div className={`mt-4 p-4 rounded-lg border ${
                    testResult.success
                      ? 'bg-green-50 border-green-200'
                      : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start space-x-2">
                      {testResult.success ? (
                        <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                      ) : (
                        <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <p className={`text-sm font-medium ${
                          testResult.success ? 'text-green-900' : 'text-red-900'
                        }`}>
                          {testResult.success ? 'Test Successful!' : 'Test Failed'}
                        </p>
                        {testResult.success && testResult.data && (
                          <div className="mt-2 text-xs space-y-1">
                            {testResult.data.totalDuration && (
                              <p className="text-green-700">
                                Total Duration: {testResult.data.totalDuration}ms
                              </p>
                            )}
                            {testResult.data.steps && (
                              <>
                                <p className="text-green-700">
                                  AI: {testResult.data.steps.ai.duration}ms ({testResult.data.steps.ai.provider})
                                </p>
                                <p className="text-green-700">
                                  TTS: {testResult.data.steps.tts.duration}ms ({testResult.data.steps.tts.provider})
                                </p>
                              </>
                            )}
                          </div>
                        )}
                        {testResult.error && (
                          <p className="text-xs text-red-700 mt-1">{testResult.error}</p>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Pipeline layer switching is managed via Admin Dashboard provider overrides */}

          {/* Realtime API Info - Show if in hybrid mode */}
          {pipelineConfig?.mode === 'hybrid' && (
            <div className="mt-6 pt-6 border-t border-gray-200">
              <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 border border-green-300 rounded-lg">
                <h3 className="text-sm font-semibold text-green-900 mb-2 flex items-center">
                  <Zap className="w-4 h-4 mr-2" />
                  OpenAI 4o Realtime API Active
                </h3>
                <p className="text-xs text-gray-700 mb-3">
                  Your system is using OpenAI&apos;s advanced Realtime API for English calls, providing ChatGPT-quality voice interactions with built-in VAD, natural interruption handling, and ultra-low latency (232-320ms).
                </p>
                <div className="bg-white bg-opacity-60 p-3 rounded">
                  <p className="text-xs font-semibold text-gray-800 mb-2">Key Benefits:</p>
                  <ul className="text-xs text-gray-700 space-y-1 list-disc list-inside">
                    <li>Native speech-to-speech (no separate STT/TTS needed)</li>
                    <li>Automatic turn detection and Voice Activity Detection</li>
                    <li>Human-like conversation flow with natural interruptions</li>
                    <li>Spanish calls automatically route to traditional pipeline</li>
                  </ul>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Test Call Queue */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <PhoneCall className="w-5 h-5 text-gray-600" />
              <h2 className="text-lg font-semibold text-gray-900">Incoming Call Queue</h2>
            </div>
            <div className="px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
              📞 Aaron → System (Incoming Tests)
            </div>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 border-b border-gray-200">
            <button
              onClick={() => setActiveQueueTab('queue')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeQueueTab === 'queue'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Queue Calls
            </button>
            <button
              onClick={() => setActiveQueueTab('manage')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeQueueTab === 'manage'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              Manage Queue ({queuedCalls.length})
            </button>
            <button
              onClick={() => setActiveQueueTab('results')}
              className={`px-4 py-2 text-sm font-medium transition-colors ${
                activeQueueTab === 'results'
                  ? 'border-b-2 border-blue-600 text-blue-600'
                  : 'text-gray-600 hover:text-gray-900'
              }`}
            >
              View Results
            </button>
          </div>
        </div>
        <div className="p-6">
          {/* Queue Calls Tab */}
          {activeQueueTab === 'queue' && (
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-2">How it works:</p>
                <p className="text-xs text-blue-800">
                  Add incoming test calls to the queue. Each call will simulate Aaron calling your system with a custom message/question. The system will respond using the OpenAI 4o Realtime API.
                </p>
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Test Name *</label>
                    <input
                      type="text"
                      value={newQueueCall.name}
                      onChange={(e) => setNewQueueCall({ ...newQueueCall, name: e.target.value })}
                      placeholder="e.g., Hours Question Test"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Description (optional)</label>
                    <input
                      type="text"
                      value={newQueueCall.description}
                      onChange={(e) => setNewQueueCall({ ...newQueueCall, description: e.target.value })}
                      placeholder="Brief description of what you're testing"
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <div>
                    <label className="text-sm font-medium text-gray-700">Schedule For (optional)</label>
                    <input
                      type="datetime-local"
                      value={newQueueCall.scheduledFor}
                      onChange={(e) => setNewQueueCall({ ...newQueueCall, scheduledFor: e.target.value })}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">Leave blank to execute immediately</p>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-700">Message/Question *</label>
                    <textarea
                      value={newQueueCall.message}
                      onChange={(e) => setNewQueueCall({ ...newQueueCall, message: e.target.value })}
                      placeholder="What will Aaron say when he calls? E.g., 'What are your business hours?' or 'I'd like to schedule an appointment'"
                      rows={6}
                      className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>

                  <button
                    onClick={addToQueue}
                    className="w-full flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    <span className="font-medium">Add to Queue</span>
                  </button>
                </div>
              </div>

              {/* Result Message */}
              {callResult && (
                <div className={`p-4 rounded-lg border ${
                  callResult.success
                    ? 'bg-green-50 border-green-200'
                    : 'bg-red-50 border-red-200'
                }`}>
                  <div className="flex items-start space-x-2">
                    {callResult.success ? (
                      <CheckCircle className="w-5 h-5 text-green-600 mt-0.5" />
                    ) : (
                      <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        callResult.success ? 'text-green-900' : 'text-red-900'
                      }`}>
                        {callResult.success ? callResult.data.message : callResult.error}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Manage Queue Tab */}
          {activeQueueTab === 'manage' && (
            <div className="space-y-4">
              {loadingQueue ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading queue...</p>
                </div>
              ) : queuedCalls.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <PhoneCall className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No calls in queue</p>
                  <p className="text-sm text-gray-500 mt-1">Add a call from the &quot;Queue Calls&quot; tab</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {queuedCalls.map((call) => (
                    <div key={call.id} className="p-4 bg-white border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <h3 className="font-semibold text-gray-900">{call.name}</h3>
                          {call.description && (
                            <p className="text-sm text-gray-600 mt-1">{call.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => executeQueuedCall(call.id)}
                            className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
                          >
                            Execute
                          </button>
                          <button
                            onClick={() => deleteQueuedCall(call.id)}
                            className="px-3 py-1 bg-red-600 text-white text-sm rounded hover:bg-red-700 transition-colors"
                          >
                            Delete
                          </button>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                        <div>
                          <p className="text-gray-500">From</p>
                          <p className="font-mono text-gray-900">{call.fromNumber}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">To</p>
                          <p className="font-mono text-gray-900">{call.toNumber}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Status</p>
                          <p className="font-medium text-gray-900">{call.status}</p>
                        </div>
                        <div>
                          <p className="text-gray-500">Scheduled</p>
                          <p className="text-gray-900">
                            {call.scheduledFor ? new Date(call.scheduledFor).toLocaleString() : 'Immediate'}
                          </p>
                        </div>
                      </div>

                      {call.flows && call.flows.length > 0 && (
                        <div className="mt-3 p-2 bg-blue-50 rounded">
                          <p className="text-xs font-semibold text-blue-900 mb-1">Message:</p>
                          <p className="text-xs text-blue-800">{call.flows[0].questionText}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Results Tab */}
          {activeQueueTab === 'results' && (
            <div className="space-y-4">
              {loadingQueue ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 mt-2">Loading results...</p>
                </div>
              ) : queueResults.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-600 font-medium">No results yet</p>
                  <p className="text-sm text-gray-500 mt-1">Execute calls from the &quot;Manage Queue&quot; tab to see results</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {queueResults.map((result) => (
                    <div key={result.id} className={`p-4 border rounded-lg ${
                      result.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'
                    }`}>
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <h3 className="font-semibold text-gray-900">{result.testCallQueue?.name}</h3>
                          <p className="text-xs text-gray-600 mt-1">
                            Executed: {new Date(result.createdAt).toLocaleString()}
                          </p>
                        </div>
                        <div className={`px-2 py-1 rounded text-xs font-medium ${
                          result.success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                        }`}>
                          {result.success ? 'Success' : 'Failed'}
                        </div>
                      </div>

                      {result.transcript && (
                        <div className="mt-2 p-2 bg-white rounded text-xs">
                          <p className="font-semibold text-gray-700 mb-1">Transcript:</p>
                          <p className="text-gray-600">{result.transcript}</p>
                        </div>
                      )}

                      {result.response && (
                        <div className="mt-2 p-2 bg-white rounded text-xs">
                          <p className="font-semibold text-gray-700 mb-1">System Response:</p>
                          <p className="text-gray-600">{result.response}</p>
                        </div>
                      )}

                      {result.errorMessage && (
                        <div className="mt-2 p-2 bg-red-100 rounded text-xs">
                          <p className="font-semibold text-red-900 mb-1">Error:</p>
                          <p className="text-red-700">{result.errorMessage}</p>
                        </div>
                      )}

                      {result.duration && (
                        <p className="text-xs text-gray-600 mt-2">
                          Duration: {result.duration}ms
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Instructions */}
        <div className="px-6 pb-6">
          <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-900 font-medium mb-2">Queue Instructions:</p>
            <ul className="text-xs text-blue-800 space-y-1 list-disc list-inside">
              <li><strong>Queue Calls:</strong> Add multiple test calls with custom messages that Aaron will say when calling the system</li>
              <li><strong>Manage Queue:</strong> View, execute, or delete queued calls. Execute calls immediately or they&apos;ll run at scheduled time</li>
              <li><strong>View Results:</strong> See transcripts and system responses from executed calls</li>
              <li><strong>Auto-execute:</strong> Scheduled calls run automatically at their specified time via the call scheduler</li>
            </ul>
          </div>
        </div>

        {/* OLD IMMEDIATE TRIGGER - Keep commented for reference */}
        {false && callResult && (
          <div className="px-6 pb-6">
            {callResult && (
              <div className={`p-3 rounded-lg border text-xs ${
                callResult.success
                  ? 'bg-green-50 border-green-200'
                  : 'bg-red-50 border-red-200'
              }`}>
                <div className="flex items-start space-x-2">
                  {callResult.success ? (
                    <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                  ) : (
                    <AlertTriangle className="w-4 h-4 text-red-600 mt-0.5 flex-shrink-0" />
                  )}
                  <div className="flex-1">
                    <p className={`font-medium ${
                      callResult.success ? 'text-green-900' : 'text-red-900'
                    }`}>
                      {callResult.success ? 'Call Initiated!' : 'Call Failed'}
                    </p>
                    {callResult.success && callResult.data && callResult.data.callId && (
                      <div className="mt-1 space-y-0.5 text-green-700">
                        <p><strong>ID:</strong> {callResult.data.callId}</p>
                        <p><strong>Status:</strong> {callResult.data.status}</p>
                        <p><strong>Direction:</strong> {callResult.data.direction}</p>
                      </div>
                    )}
                    {callResult.error && (
                      <p className="text-red-700 mt-1">{callResult.error}</p>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Real-time Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Activity className="w-8 h-8 text-blue-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Active Calls</p>
              <p className="text-2xl font-bold text-gray-900">{realTimeData.activeCalls}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Cpu className="w-8 h-8 text-green-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">CPU Usage</p>
              <p className="text-2xl font-bold text-gray-900">{realTimeData.cpuUsage}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <HardDrive className="w-8 h-8 text-purple-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Memory Usage</p>
              <p className="text-2xl font-bold text-gray-900">{realTimeData.memoryUsage}%</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center">
            <Network className="w-8 h-8 text-orange-600" />
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Network I/O</p>
              <p className="text-2xl font-bold text-gray-900">{realTimeData.networkIn}MB/s</p>
            </div>
          </div>
        </div>
      </div>

      {/* Service Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Service Status</h2>
        </div>
        <div className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {systemStatus.services.map((service, index) => (
              <div key={index} className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-gray-900">{service.name}</h3>
                  <div className={`flex items-center space-x-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(service.status)}`}>
                    {getStatusIcon(service.status)}
                    <span className="capitalize">{service.status}</span>
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Uptime:</span>
                    <span className="font-medium">{service.uptime}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Response Time:</span>
                    <span className="font-medium">{service.responseTime}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* System Metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">System Overview</h2>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-600">Total Calls Today:</span>
                <span className="font-medium">{systemStatus.metrics.totalCalls?.toLocaleString()}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Active Calls:</span>
                <span className="font-medium">{systemStatus.metrics.activeCalls}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Failure Rate:</span>
                <span className="font-medium">{systemStatus.metrics.failureRate}%</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Avg Response Time:</span>
                <span className="font-medium">{systemStatus.metrics.avgResponseTime}ms</span>
              </div>
            </div>
          </div>
        </div>

        {/* Alerts */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">Recent Alerts</h2>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {systemStatus.alerts.length > 0 ? (
                systemStatus.alerts.map((alert) => (
                  <div key={alert.id} className={`border rounded-lg p-3 ${getAlertColor(alert.level)}`}>
                    <div className="flex items-start space-x-2">
                      <AlertTriangle className={`w-4 h-4 mt-0.5 ${
                        alert.level === 'error' ? 'text-red-600' :
                          alert.level === 'warning' ? 'text-yellow-600' : 'text-blue-600'
                      }`} />
                      <div className="flex-1">
                        <p className="text-sm font-medium text-gray-900">{alert.message}</p>
                        <p className="text-xs text-gray-600 mt-1">
                          {alert.timestamp.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-500 text-center py-4">No recent alerts</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MonitoringPage;
